import { db, storage } from '../firebase';
import { collection, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';

/**
 * Service to manage reward items from Firestore and Firebase Storage
 */

/**
 * Get download URL for a reward item image from Firebase Storage
 * @param {string} fileName - The file name in storage (e.g., "typewriter.png")
 * @returns {Promise<string>} - The download URL
 */
export const getRewardImageUrl = async (fileName) => {
  try {
    const storageRef = ref(storage, fileName);
    const url = await getDownloadURL(storageRef);
    return url;
  } catch (error) {
    console.error(`Error fetching image URL for ${fileName}:`, error);
    throw error;
  }
};

/**
 * Get all available reward items from Firestore
 * @returns {Promise<Array>} - Array of reward item objects
 */
export const getAllRewardItems = async () => {
  try {
    const rewardItemsRef = collection(db, 'reward_items');
    const snapshot = await getDocs(rewardItemsRef);
    
    const items = [];
    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      
      // Use cached imageUrl from Firestore if available, otherwise fetch from Storage
      let imageUrl = data.imageUrl; // Check for cached URL first
      
      if (!imageUrl && data.storagePath) {
        // Fallback: Fetch from Storage if no cached URL (for backwards compatibility)
        try {
          imageUrl = await getRewardImageUrl(data.storagePath);
          console.warn(`[RewardItems] No cached URL for ${data.name}, fetched from Storage`);
        } catch (error) {
          console.error(`[RewardItems] Failed to load image for ${data.name}:`, error.code || error.message);
          // Don't skip - add item anyway with placeholder
          imageUrl = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100" height="100" fill="gray"/><text x="50" y="50" text-anchor="middle" fill="white">Error</text></svg>';
        }
      }
      
      items.push({
        id: docSnap.id,
        name: data.name,
        itemType: data.itemType || 'desk_item', // Default to desk_item for backwards compatibility
        imageUrl: imageUrl,
        storagePath: data.storagePath, // Keep for backwards compatibility
        // Video background fields
        vimeoId: data.vimeoId,
        vimeoHash: data.vimeoHash,
        // Audio track fields
        fileName: data.fileName,
        unlockRequirement: data.unlockRequirement || { type: 'default' },
        order: data.order || 0
      });
    }
    
    // Sort by order
    items.sort((a, b) => a.order - b.order);
    
    return items;
  } catch (error) {
    console.error('[RewardItems] Error fetching reward items:', error);
    return [];
  }
};

/**
 * Check if a reward item should be unlocked for a user based on their progress
 * @param {object} unlockRequirement - The unlock requirement object
 * @param {object} userProgress - The user's progress data
 * @returns {boolean} - Whether the item should be unlocked
 */
export const isItemUnlocked = (unlockRequirement, userProgress) => {
  if (!unlockRequirement) return false;
  
  switch (unlockRequirement.type) {
    case 'default':
      // Always unlocked (like typewriter)
      return true;
      
    case 'level':
      // Unlock when user completes a specific level in any course
      // userProgress should contain completed levels count
      const completedLevels = userProgress?.completedLevels || 0;
      return completedLevels >= unlockRequirement.value;
      
    case 'course':
      // Unlock when user completes a specific course
      const completedCourses = userProgress?.completedCourses || [];
      return completedCourses.includes(unlockRequirement.courseId);
      
    case 'prompt':
      // Unlock when user completes a certain number of prompts
      const completedPrompts = userProgress?.completedPrompts || 0;
      return completedPrompts >= unlockRequirement.value;
      
    default:
      return false;
  }
};

/**
 * Get user's progress data for unlocking reward items
 * @param {string} userId - The user's ID
 * @returns {Promise<object>} - User progress data
 */
export const getUserProgress = async (userId) => {
  try {
    // Get completed levels count
    const userLevelsRef = collection(db, 'user_levels');
    const levelsSnapshot = await getDocs(userLevelsRef);
    const completedLevels = levelsSnapshot.docs.filter(doc => 
      doc.id.startsWith(userId) && doc.data().completed === true
    ).length;
    
    // Get completed courses
    const userCoursesRef = collection(db, 'user_courses');
    const coursesSnapshot = await getDocs(userCoursesRef);
    const completedCourses = coursesSnapshot.docs
      .filter(doc => doc.id.startsWith(userId) && doc.data().completed === true)
      .map(doc => doc.data().courseId);
    
    // Get completed prompts count
    const userPromptsRef = collection(db, 'user_prompts');
    const promptsSnapshot = await getDocs(userPromptsRef);
    const completedPrompts = promptsSnapshot.docs.filter(doc => 
      doc.id.startsWith(userId) && doc.data().completed === true
    ).length;
    
    return {
      completedLevels,
      completedCourses,
      completedPrompts
    };
  } catch (error) {
    console.error('Error fetching user progress:', error);
    return {
      completedLevels: 0,
      completedCourses: [],
      completedPrompts: 0
    };
  }
};

/**
 * Get user's reward items preferences from Firestore
 * @param {string} userId - The user's ID
 * @returns {Promise<object>} - User reward items data including selectedScene and items array
 */
export const getUserRewardItems = async (userId) => {
  try {
    const userRewardItemsRef = doc(db, 'user_reward_items', userId);
    const userRewardItemsDoc = await getDoc(userRewardItemsRef);
    
    if (userRewardItemsDoc.exists()) {
      return userRewardItemsDoc.data();
    }
    
    // Return default structure if document doesn't exist
    return {
      items: [],
      selectedScene: null
    };
  } catch (error) {
    console.error('[RewardItems] Error fetching user reward items:', error);
    return {
      items: [],
      selectedScene: null
    };
  }
};

/**
 * Save user's reward items preferences to Firestore
 * @param {string} userId - The user's ID
 * @param {object} data - The data to save (can include items array, selectedScene, etc.)
 * @returns {Promise<void>}
 */
export const saveUserRewardItems = async (userId, data) => {
  try {
    const userRewardItemsRef = doc(db, 'user_reward_items', userId);
    
    // Get existing data to preserve fields we're not updating
    const existingDoc = await getDoc(userRewardItemsRef);
    const existingData = existingDoc.exists() ? existingDoc.data() : {};
    
    // Merge existing data with new data
    const updatedData = {
      ...existingData,
      ...data,
      lastUpdated: new Date().toISOString()
    };
    
    await setDoc(userRewardItemsRef, updatedData);
  } catch (error) {
    console.error('[RewardItems] Error saving user reward items:', error);
    throw error;
  }
};

/**
 * Save the selected scene for a user
 * @param {string} userId - The user's ID
 * @param {string} sceneName - The name of the selected scene
 * @returns {Promise<void>}
 */
export const saveSelectedScene = async (userId, sceneName) => {
  return saveUserRewardItems(userId, { selectedScene: sceneName });
};

